import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface FilePatient {
  id: string;
  salle: 'chef' | 'garde';
  ticket_number: string;
  niveau_urgence: 'urgent' | 'prioritaire' | 'normal';
  statut: 'en_attente' | 'en_cours';
}

const URGENCE_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  urgent: { bg: 'bg-red-500', text: 'text-red-700', label: 'Urgent' },
  prioritaire: { bg: 'bg-amber-500', text: 'text-amber-700', label: 'Prioritaire' },
  normal: { bg: 'bg-clinic-blue', text: 'text-clinic-blue-dark', label: 'Normal' },
};

function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.setValueAtTime(1000, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.25);
    setTimeout(() => ctx.close(), 400);
  } catch (_) { /* audio non supporté */ }
}

export default function AccueilFileAttente() {
  const [files, setFiles] = useState<FilePatient[]>([]);
  const [loading, setLoading] = useState(true);

  const loadFiles = async () => {
    try {
      const { data, error } = await supabase
        .from('file_attente')
        .select('id, salle, ticket_number, niveau_urgence, statut')
        .order('salle', { ascending: true })
        .order('ticket_seq', { ascending: true });
      if (error) throw error;
      setFiles((data || []) as FilePatient[]);
    } catch (e) {
      console.error('Erreur chargement file attente:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();

    const channelName = `accueil_ft_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'file_attente' }, (payload) => {
        loadFiles();
        // Jouer un son quand un patient est appelé (statut passe à en_cours)
        if (payload.eventType === 'UPDATE' && (payload.new as any)?.statut === 'en_cours') {
          playNotificationSound();
        }
        // Jouer un son quand un nouveau patient est ajouté
        if (payload.eventType === 'INSERT') {
          playNotificationSound();
        }
      })
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn('[AccueilFileAttente] Realtime channel error, reloading...');
          loadFiles();
        }
      });

    // Polling de secours toutes les 10s
    const pollingInterval = setInterval(() => {
      loadFiles();
    }, 10000);

    return () => {
      clearInterval(pollingInterval);
      supabase.removeChannel(channel);
    };
  }, []);

  const enCoursChef = files.find(f => f.salle === 'chef' && f.statut === 'en_cours');
  const enCoursGarde = files.find(f => f.salle === 'garde' && f.statut === 'en_cours');
  const attenteChef = files.filter(f => f.salle === 'chef' && f.statut === 'en_attente');
  const attenteGarde = files.filter(f => f.salle === 'garde' && f.statut === 'en_attente');

  if (loading) return null;

  if (files.length === 0) {
    return (
      <section className="py-8 md:py-12 bg-gradient-to-br from-amber-50/50 via-white to-teal-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-8 md:py-10">
            <div className="w-24 h-24 md:w-28 md:h-28 flex items-center justify-center mx-auto mb-4">
              <img
                src="https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/brand/clinic.jpeg"
                alt="Logo Clinique Sainte Famille"
                className="w-full h-full object-contain rounded-full"
              />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-500 mb-1">Bienvenue à la</h3>
            <h2 className="text-xl md:text-3xl font-black text-clinic-green tracking-wide">Clinique Sainte Famille</h2>
            <div className="mt-4 w-12 h-0.5 bg-clinic-green/30 mx-auto rounded-full"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-6 md:py-10 bg-gradient-to-br from-amber-50/50 via-white to-teal-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6">
          <h2 className="text-lg md:text-2xl font-bold text-gray-900">Salle d'attente</h2>
          <p className="text-xs md:text-sm text-gray-500 mt-1">Ordre de passage en temps réel</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {/* Salle Dr OLYMPIO Camille */}
          <div className="bg-white rounded-2xl border-2 border-amber-200 p-4 md:p-5">
            <h3 className="text-xs font-bold text-amber-700 uppercase tracking-widest mb-3 text-center">
              Dr OLYMPIO Camille
            </h3>

            {enCoursChef ? (
              <div className="bg-clinic-blue-dark rounded-xl p-3 md:p-4 text-white text-center mb-3 animate-fadeIn">
                <p className="text-xs text-clinic-blue/80 font-medium">En consultation</p>
                <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-xl flex items-center justify-center mx-auto my-2">
                  <span className="text-lg md:text-xl font-black text-clinic-blue-dark">{enCoursChef.ticket_number}</span>
                </div>
                <span className="inline-block bg-clinic-green text-white px-4 py-1 rounded-full text-xs font-bold animate-pulse">
                  En cours
                </span>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-4 text-center border border-dashed border-gray-200 mb-3">
                <p className="text-xs text-gray-400">En attente de patient</p>
              </div>
            )}

            <p className="text-xs font-semibold text-gray-400 uppercase mb-2">File d'attente ({attenteChef.length})</p>
            {attenteChef.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-2">Aucun patient</p>
            ) : (
              <div className="space-y-1.5">
                {attenteChef.slice(0, 5).map((f) => {
                  const badge = URGENCE_BADGE[f.niveau_urgence];
                  return (
                    <div key={f.id} className="flex items-center gap-2 p-2 rounded-lg bg-amber-50/50 border border-amber-100">
                      <span className={`w-8 h-8 ${badge.bg} rounded-md flex items-center justify-center flex-shrink-0`}>
                        <span className="text-xs font-black text-white">{f.ticket_number}</span>
                      </span>
                      <span className={`text-xs font-medium ${badge.text}`}>{badge.label}</span>
                    </div>
                  );
                })}
                {attenteChef.length > 5 && (
                  <p className="text-xs text-gray-400 text-center">+{attenteChef.length - 5} autres</p>
                )}
              </div>
            )}
          </div>

          {/* Salle Médecins de Garde */}
          <div className="bg-white rounded-2xl border-2 border-teal-200 p-4 md:p-5">
            <h3 className="text-xs font-bold text-teal-700 uppercase tracking-widest mb-3 text-center">
              Médecins de Garde
            </h3>

            {enCoursGarde ? (
              <div className="bg-clinic-blue-dark rounded-xl p-3 md:p-4 text-white text-center mb-3 animate-fadeIn">
                <p className="text-xs text-clinic-blue/80 font-medium">En consultation</p>
                <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-xl flex items-center justify-center mx-auto my-2">
                  <span className="text-lg md:text-xl font-black text-clinic-blue-dark">{enCoursGarde.ticket_number}</span>
                </div>
                <span className="inline-block bg-clinic-green text-white px-4 py-1 rounded-full text-xs font-bold animate-pulse">
                  En cours
                </span>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-4 text-center border border-dashed border-gray-200 mb-3">
                <p className="text-xs text-gray-400">En attente de patient</p>
              </div>
            )}

            <p className="text-xs font-semibold text-gray-400 uppercase mb-2">File d'attente ({attenteGarde.length})</p>
            {attenteGarde.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-2">Aucun patient</p>
            ) : (
              <div className="space-y-1.5">
                {attenteGarde.slice(0, 5).map((f) => {
                  const badge = URGENCE_BADGE[f.niveau_urgence];
                  return (
                    <div key={f.id} className="flex items-center gap-2 p-2 rounded-lg bg-teal-50/50 border border-teal-100">
                      <span className={`w-8 h-8 ${badge.bg} rounded-md flex items-center justify-center flex-shrink-0`}>
                        <span className="text-xs font-black text-white">{f.ticket_number}</span>
                      </span>
                      <span className={`text-xs font-medium ${badge.text}`}>{badge.label}</span>
                    </div>
                  );
                })}
                {attenteGarde.length > 5 && (
                  <p className="text-xs text-gray-400 text-center">+{attenteGarde.length - 5} autres</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}